import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { runBatch, summarizeBatch } from "../../utils/runBatch";
import {
  describeQualifyingScore,
  dtoToSavePayload,
} from "../../utils/qualifyingScoreUtils";
import {
  getQualifyingScore,
  postQualifyingScore,
} from "../../pages/QualifyingScores/sliceReducer";

// Few enough to stay gentle on the API, enough that 50 rows take seconds, not a minute.
const CONCURRENCY = 3;

const useStyles = makeStyles({
  wrapper: {
    minWidth: "440px",
    maxWidth: "560px",
    "@media (max-width: 600px)": { minWidth: "280px" },
  },
  note: { fontSize: "12px", color: "#667085", marginTop: "8px" },
  progressLine: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginTop: "8px",
  },
  bar: { height: "8px", borderRadius: "4px", marginTop: "12px" },
  counts: { display: "flex", gap: "16px", marginTop: "12px", fontSize: "13px" },
  ok: { color: "#067647" },
  failed: { color: "#b42318" },
  muted: { color: "#667085" },
  failures: {
    marginTop: "12px",
    maxHeight: "200px",
    overflowY: "auto",
    border: "1px solid #fda29b",
    borderRadius: "6px",
    backgroundColor: "#fef3f2",
    padding: "8px 12px",
    fontSize: "12px",
  },
  failureRow: { padding: "4px 0" },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px",
  },
});

const formatSeconds = (s) => {
  if (s < 60) return `${Math.max(1, Math.round(s))}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

/**
 * Applies one message template to many qualifying scores, one request per score, so a failure
 * on one row is reported and the rest still save. Each row is re-read right before it is
 * written, because the save replaces every field: writing from the (possibly stale) list could
 * undo someone else's edit.
 */
const QualifyingScoreBulkTemplateDialog = ({
  ids,
  rowsById,
  templates,
  templatesLoading,
  onRunningChange,
  onFinished,
  onClose,
}) => {
  const classes = useStyles();
  const [templateId, setTemplateId] = useState("");
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("pick"); // pick | running | done
  const [progress, setProgress] = useState({ finished: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [stopping, setStopping] = useState(false);
  const stopRef = useRef(false);
  const startedAtRef = useRef(0);
  const labelsRef = useRef({});

  const activeTemplates = useMemo(
    () => (templates || []).filter((t) => t.active !== false),
    [templates],
  );

  const alreadyUsing = useMemo(
    () =>
      templateId === ""
        ? 0
        : ids.filter((id) => rowsById[id]?.messageTemplateId === templateId).length,
    [ids, rowsById, templateId],
  );

  const labelFor = (id) =>
    labelsRef.current[id] || describeQualifyingScore(rowsById[id]) || `Score #${id}`;

  const setRunning = (running) => {
    setPhase(running ? "running" : "done");
    if (onRunningChange) onRunningChange(running);
  };

  const run = async (runIds) => {
    stopRef.current = false;
    setStopping(false);
    setResults([]);
    setProgress({ finished: 0, total: runIds.length });
    startedAtRef.current = Date.now();
    setRunning(true);

    const batch = await runBatch(
      runIds,
      async (id) => {
        const dto = await getQualifyingScore(id);
        labelsRef.current[id] = describeQualifyingScore(dto);
        if (dto?.messageTemplateId === templateId) return runBatch.SKIPPED;
        return postQualifyingScore(dtoToSavePayload(dto, { messageTemplateId: templateId }));
      },
      {
        concurrency: CONCURRENCY,
        shouldStop: () => stopRef.current,
        onProgress: ({ finished, total }) => setProgress({ finished, total }),
      },
    );

    setResults(batch);
    setRunning(false);
    if (onFinished) {
      const saved = batch.filter((r) => r.status === "done" && r.value).map((r) => r.value);
      onFinished(summarizeBatch(batch), saved);
    }
  };

  const handleStart = () => {
    if (templateId === "") {
      setError("Choose a template");
      return;
    }
    setError("");
    run(ids);
  };

  const handleStop = () => {
    stopRef.current = true;
    setStopping(true);
  };

  const summary = summarizeBatch(results);
  const failedResults = results.filter((r) => r?.status === "failed");
  const cancelledIds = results.filter((r) => r?.status === "cancelled").map((r) => r.item);
  const templateLabel =
    activeTemplates.find((t) => t.id === templateId)?.description || `Template ${templateId}`;

  if (phase === "pick") {
    return (
      <Box className={classes.wrapper}>
        <Typography variant="body1">
          Set the message template on <b>{ids.length}</b> qualifying score
          {ids.length === 1 ? "" : "s"}.
        </Typography>

        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          style={{ marginTop: 16 }}
          error={Boolean(error)}
        >
          <InputLabel>Message template</InputLabel>
          <Select
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setError("");
            }}
            label="Message template"
            disabled={templatesLoading}
          >
            {activeTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.description || `Template ${t.id}`}
                {t.localeIds?.length ? (
                  <span className={classes.muted}>&nbsp;· {t.localeIds.join(", ")}</span>
                ) : null}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
          {!error && !templatesLoading && activeTemplates.length === 0 && (
            <FormHelperText>No active qualifying-score templates.</FormHelperText>
          )}
        </FormControl>

        {alreadyUsing > 0 && (
          <Typography className={classes.note}>
            {alreadyUsing} already use{alreadyUsing === 1 ? "s" : ""} this template and will be
            skipped.
          </Typography>
        )}
        <Typography className={classes.note}>
          Each score is saved separately. If one fails, the others still save and you can retry
          the failures.
        </Typography>

        <Box className={classes.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleStart}
            disabled={ids.length === 0 || templatesLoading}
          >
            Apply to {ids.length}
          </Button>
        </Box>
      </Box>
    );
  }

  const { finished, total } = progress;
  const percent = total ? Math.round((finished / total) * 100) : 0;
  const elapsed = (Date.now() - startedAtRef.current) / 1000;
  const remaining =
    phase === "running" && finished > 0 ? (elapsed / finished) * (total - finished) : null;

  return (
    <Box className={classes.wrapper}>
      <Typography variant="body1">
        {phase === "running" ? "Applying" : "Applied"} <b>{templateLabel}</b>
      </Typography>

      <LinearProgress
        className={classes.bar}
        variant="determinate"
        value={percent}
        aria-label="Progress"
      />
      <Box className={classes.progressLine}>
        <span>
          {finished} of {total}
        </span>
        <span className={classes.muted}>
          {phase === "running"
            ? stopping
              ? "Stopping after the rows in progress…"
              : remaining != null
              ? `about ${formatSeconds(remaining)} left`
              : "Starting…"
            : `Finished in ${formatSeconds(elapsed)}`}
        </span>
      </Box>

      {phase === "done" && (
        <>
          <Box className={classes.counts}>
            <span className={classes.ok}>{summary.done} updated</span>
            {summary.skipped > 0 && (
              <span className={classes.muted}>{summary.skipped} already set</span>
            )}
            {summary.failed > 0 && (
              <span className={classes.failed}>{summary.failed} failed</span>
            )}
            {summary.cancelled > 0 && (
              <span className={classes.muted}>{summary.cancelled} not started</span>
            )}
          </Box>

          {failedResults.length > 0 && (
            <Box className={classes.failures} role="list" aria-label="Failed rows">
              {failedResults.map((r) => (
                <div key={r.item} className={classes.failureRow} role="listitem">
                  <b>{labelFor(r.item)}</b> — {r.error}
                </div>
              ))}
            </Box>
          )}
        </>
      )}

      <Box className={classes.actions}>
        {phase === "running" ? (
          <Button onClick={handleStop} disabled={stopping}>
            Stop
          </Button>
        ) : (
          <>
            {cancelledIds.length > 0 && (
              <Button onClick={() => run(cancelledIds)}>
                Continue ({cancelledIds.length})
              </Button>
            )}
            {failedResults.length > 0 && (
              <Button onClick={() => run(failedResults.map((r) => r.item))}>
                Retry failed ({failedResults.length})
              </Button>
            )}
            <Button color="primary" variant="contained" onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

QualifyingScoreBulkTemplateDialog.propTypes = {
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  rowsById: PropTypes.object,
  templates: PropTypes.array,
  templatesLoading: PropTypes.bool,
  onRunningChange: PropTypes.func,
  onFinished: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

QualifyingScoreBulkTemplateDialog.defaultProps = {
  rowsById: {},
  templates: [],
  templatesLoading: false,
};

export default QualifyingScoreBulkTemplateDialog;
